import { RemovableCard, Text } from '@dixa/lib-shared-habla';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { HookFormQueuesSelect } from 'components/hookForm';

import {
  BulkModalActionBaseProps,
  BulkModalTransferToQueueAction,
} from '../../../types/conversationsBulkModal.types';
import { conversationsBulkModalActionLabel } from '../../../utils/conversationsBulkModalActionLabel';

type ConversationsBulkModalTransferToQueueActionProps =
  BulkModalActionBaseProps<BulkModalTransferToQueueAction>;

/**
 * Represents the component for the conversations bulk modal transfer to queue
 * action.
 */
export const ConversationsBulkModalTransferToQueueAction = ({
  conversationIds,
  onRemove,
  onChange,
}: ConversationsBulkModalTransferToQueueActionProps): JSX.Element => {
  const form = useForm<{ queueId: string[] }>({
    defaultValues: { queueId: [] },
  });

  const queueIdValue = useWatch({ control: form.control, name: 'queueId' });

  useEffect(() => {
    const selectedQueueId = queueIdValue?.[0];

    onChange({
      type: 'transfer_to_queue',
      isValid: !!selectedQueueId,
      arg: {
        csids: conversationIds,
        // Note: returns empty string if the queue id is undefined, which also
        // means that the action would be invalid which would guard the
        // ability to perform bulk actions by the modal.
        queueId: selectedQueueId ?? '',
      },
    });
  }, [conversationIds, onChange, queueIdValue]);

  return (
    <RemovableCard
      icon="arrow-right"
      title={conversationsBulkModalActionLabel['transfer_to_queue']}
      onRemove={() => onRemove('transfer_to_queue')}
    >
      <Text.Paragraph subdued withMarginBottom>
        Transferring conversations to a different queue will cause all those
        conversations to open and be available for offering in the new queue. If
        you would like to avoid that and close the selected conversations as
        well, then you can create a new bulk action afterwards and use the
        “Close” action.
      </Text.Paragraph>

      <HookFormQueuesSelect
        name="queueId"
        control={form.control}
        noAlert
        noLabel
      />
    </RemovableCard>
  );
};
